import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  LinearProgress,
  Toolbar,
  Divider,
} from "@mui/material";
import Api, { APIs } from "../../../Api";

// ---- Types ----
export type PPPProject = {
  sr_no: number | null;
  name: string | null;
  implementing_agency: string | null;
  approving_forum: string | null;
  status: string | null;
  ppp_2025_26_est_usd_m: number | null;
};

export type PPPProjectsTableProps = {
  title?: string;
};

const DOLLAR_PKR_RATE = 280;

const fmt = (n: number | null | undefined) =>
  n === null || n === undefined
    ? "–"
    : n.toLocaleString(undefined, { maximumFractionDigits: 2 });

function computeTotals(rows: PPPProject[]) {
  const sum = (key: keyof PPPProject) =>
    rows.reduce(
      (acc, r) => acc + (typeof r[key] === "number" ? (r[key] as number) : 0),
      0,
    );

  const a2526_PKR_Billion = sum("ppp_2025_26_est_usd_m");
  const a2526_USD_Million = (a2526_PKR_Billion * 1000) / DOLLAR_PKR_RATE;

  return {
    a2526_PKR_Billion,
    a2526_USD_Million,
  };
}

// Height for the scrollable region (adjust to taste)
const VIEWPORT_HEIGHT = "70vh";

export default function PPPProjectsTable({
  title = "PPP Project 4RF Framework FY25-26",
}: PPPProjectsTableProps) {
  const [rows, setRows] = useState<PPPProject[] | null>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    Api.get(APIs.MoPD_PPP_PROJECTS)
      .then((payload) => {
        console.log("PPP Projects", payload);
        if (payload) setRows(payload);
      })
      .catch((e: any) => setError(e?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const displayRows = rows ?? [];
  const totals = useMemo(() => computeTotals(displayRows), [displayRows]);

  return (
    <Paper
      elevation={1}
      className="p-4"
      sx={{
        height: "80vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
      }}
    >
      <Toolbar disableGutters sx={{ mb: 1, justifyContent: "space-between" }}>
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
          US$ Million
        </Typography>
      </Toolbar>
      <Divider sx={{ mb: 2 }} />

      {loading && <LinearProgress />}
      {error && (
        <Box sx={{ color: "error.main", mb: 1 }}>Failed to load: {error}</Box>
      )}

      {/* Scrollable table */}
      <TableContainer
        sx={{
          flex: 1,
          overflow: "auto",
          borderRadius: 2,
          border: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={th}>Sr. No.</TableCell>
              <TableCell sx={thCenter}>Project Name</TableCell>
              <TableCell sx={thCenter}>Implementing Agency</TableCell>
              <TableCell sx={thRight}>Estimated Cost (PKR. bn)</TableCell>
              <TableCell sx={thCenter}>Approving Forum</TableCell>
              <TableCell sx={thCenter}>Current Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.map((r, idx) => (
              <TableRow key={idx} hover>
                <TableCell sx={tdCenter}>{r.sr_no ?? idx + 1}</TableCell>
                <TableCell sx={{ ...td, fontWeight: 600 }}>
                  {r.name ?? "–"}
                </TableCell>
                <TableCell sx={tdCenter}>{r.implementing_agency}</TableCell>
                <TableCell sx={tdRight}>
                  {fmt(r.ppp_2025_26_est_usd_m)}
                </TableCell>
                <TableCell sx={tdCenter}>{r.approving_forum}</TableCell>
                <TableCell sx={tdCenter}>{r.status}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={totalCell} colSpan={2}>
                Total PKR Billion
              </TableCell>
              <TableCell sx={totalNum} colSpan={2}>
                {fmt(totals.a2526_PKR_Billion)}
              </TableCell>
              <TableCell sx={totalCell} colSpan={2}></TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={totalCell} colSpan={2}>
                Total US$ Million
              </TableCell>
              <TableCell sx={totalNum} colSpan={2}>
                {fmt(totals.a2526_USD_Million)}
              </TableCell>
              <TableCell sx={totalCell} colSpan={2}></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer fixed below */}
      <Box
        sx={{
          mt: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          Updated on: December 29, 2025
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          Source: RRU / MoPD&SI
        </Typography>
      </Box>
    </Paper>
  );
}

// ---- Cell styles (MUI SX) ----
const base = {
  borderRight: (t: any) => `1px solid ${t.palette.divider}`,
  borderBottom: (t: any) => `1px solid ${t.palette.divider}`,
};
const th = {
  ...base,
  // backgroundColor: (t: any) => t.palette.success.light + "22",
  backgroundColor: (t: any) => t.palette.success.light + "dd",
  fontWeight: 700,
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 2,
};
const thRight = { ...th, textAlign: "right" };
const thCenter = { ...th, textAlign: "center" };
const td = { ...base };
const tdRight = { ...td, textAlign: "right" };
const tdCenter = { ...td, textAlign: "center" };
const totalCell = {
  ...base,
  fontWeight: 800,
  textAlign: "right",
  backgroundColor: (t: any) => t.palette.grey[100],
};
const totalNum = { ...totalCell, width: 140 };
